import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  ChevronLeft,
  RefreshCw,
  Loader2,
  Search,
  Mail,
  Phone,
  Clock,
  Wallet,
  Download,
  AlertCircle,
  Pencil,
  Trash2,
  ShieldAlert,
  X,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import {
  useAccountsAwaitingApplicationQuery,
  useGetActiveUserCategoriesQuery,
  type AwaitingApplicationRow,
} from "../api/probationerApplicationApi";
/*
 * The member desk's own endpoints, reused rather than copied.
 *
 * These accounts are ordinary user accounts; correcting an address or deleting
 * a duplicate signup is the same act here as it is on the member register, and
 * the server applies the same rules to both. A second pair of endpoints scoped
 * to this screen would be a second set of rules to keep in step.
 */
import {
  useDiscardDuplicateAccountMutation,
  useUpdateUserEmailMutation,
} from "../../membership/api/membershipApi";

/**
 * Accounts that registered and never filled the entry form in.
 *
 * Every other screen in this module is a list of APPLICATIONS, which is
 * precisely why these people go unseen: they have none. They signed up, were
 * invoiced, and stopped. This is the only place they appear, so it is built to
 * be WORKED — longest wait first, with the phone number and email on the row —
 * rather than merely browsed.
 *
 * The fee is given its own column because it splits the list in two. Someone
 * who paid and never filed got lost inside the eight-step form and is worth a
 * phone call; someone who never paid has only opened an account.
 */
function AwaitingApplicationScreen() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [fee, setFee] = useState<"" | "paid" | "unpaid">("");
  const [sort, setSort] = useState<"waiting" | "recent">("waiting");
  const [page, setPage] = useState(1);

  // Typing is debounced into the request: a keystroke per fetch would re-run
  // the counts over every account on every letter.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching, isError, refetch } =
    useAccountsAwaitingApplicationQuery({
      q,
      category_id: categoryId,
      fee,
      sort,
      page,
      per_page: 25,
    });

  const { data: categories = [] } = useGetActiveUserCategoriesQuery();

  /*
   * The two things the desk does to an account after the call.
   *
   * Which of them is offered is decided by the row, not by the screen: the
   * server sends the reasons an account may not be deleted with it, and an
   * account the Institute has been paid for is corrected rather than removed.
   */
  const [emailFor, setEmailFor] = useState<AwaitingApplicationRow | null>(null);
  const [deleteFor, setDeleteFor] = useState<AwaitingApplicationRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const summary = data?.summary ?? { total: 0, fee_paid: 0 };
  const meta = data?.meta ?? { current_page: 1, last_page: 1, per_page: 25, total: 0 };

  // Only the grades that enter through this form — the rest of the list is
  // Fellow, Member and Firm, none of which can ever appear below.
  const entryCategories = useMemo(
    () =>
      categories.filter((c) =>
        ["probationer", "technician", "student"].includes((c.name ?? "").toLowerCase())
      ),
    [categories]
  );

  /**
   * The current page as a file, for whoever is doing the calling.
   *
   * Built in the browser from rows already in hand rather than through an
   * export endpoint: the list is a worksheet, not a record, and it is read on
   * the day it is taken.
   */
  const downloadCsv = () => {
    if (!rows.length) return;

    const header = ["Name", "Email", "Phone", "Grade applied for", "Registered", "Days waiting", "Application fee"];
    const body = rows.map((r: AwaitingApplicationRow) => [
      r.name ?? "",
      r.email ?? "",
      r.phone ?? "",
      r.category ?? "",
      r.registered_at ?? "",
      r.days_waiting ?? "",
      r.fee_paid ? "Paid" : "Not paid",
    ]);

    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    // The BOM is what makes Excel open this as UTF-8.
    const csv =
      "﻿" + [header, ...body].map((row) => row.map(esc).join(",")).join("\r\n");

    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `accounts-awaiting-application-page-${meta.current_page}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">
              Accounts without an application
            </h1>
            <p className="text-sm font-semibold text-gray-500">
              Signed up but never filled the entry form in
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={downloadCsv}
            disabled={!rows.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-[10px] font-black uppercase tracking-widest hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> Download page
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-[10px] font-black uppercase tracking-widest hover:text-gray-700 hover:bg-gray-50"
          >
            {isFetching ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Figures, counted over everyone outstanding rather than over the
          current filter, so they hold still while the desk searches. */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Outstanding" value={summary.total} tone="amber" />
        <Stat
          label="Fee paid, no form"
          value={summary.fee_paid}
          tone="rose"
          hint="Paid and then stopped — call these first"
        />
        <Stat
          label="Never paid"
          value={Math.max(summary.total - summary.fee_paid, 0)}
          tone="gray"
          hint="Opened an account and went no further"
        />
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or phone"
            className="w-full border rounded-xl pl-9 pr-3 py-2.5 text-sm"
          />
        </div>

        <select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value ? Number(e.target.value) : "");
            setPage(1);
          }}
          className="border rounded-xl px-3 py-2.5 text-sm bg-white"
        >
          <option value="">All grades</option>
          {entryCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          value={fee}
          onChange={(e) => {
            setFee(e.target.value as "" | "paid" | "unpaid");
            setPage(1);
          }}
          className="border rounded-xl px-3 py-2.5 text-sm bg-white"
        >
          <option value="">Paid or not</option>
          <option value="paid">Fee paid</option>
          <option value="unpaid">Fee not paid</option>
        </select>

        <select
          value={sort}
          onChange={(e) => {
            setSort(e.target.value as "waiting" | "recent");
            setPage(1);
          }}
          className="border rounded-xl px-3 py-2.5 text-sm bg-white"
        >
          <option value="waiting">Longest waiting first</option>
          <option value="recent">Newest first</option>
        </select>
      </div>

      {isError && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          Could not load the list. Try Refresh.
        </div>
      )}

      {/* What the last action did, kept until it is dismissed: a row that has
          just been deleted leaves the table, and without this the screen would
          simply be one row shorter with nothing said. */}
      {notice && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="mt-6 h-64 bg-gray-50 rounded-2xl animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="mt-6 border rounded-2xl p-10 text-center">
          <p className="text-sm font-bold text-gray-700">Nobody is waiting.</p>
          <p className="text-xs font-semibold text-gray-400 mt-1">
            Every entry-grade account has an application behind it.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  <th className="text-left px-4 py-3">Account</th>
                  <th className="text-left px-4 py-3">Grade</th>
                  <th className="text-left px-4 py-3">Registered</th>
                  <th className="text-left px-4 py-3">Waiting</th>
                  <th className="text-left px-4 py-3">Application fee</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r: AwaitingApplicationRow) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-bold text-gray-900">{r.name || "—"}</div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            <Mail className="w-3 h-3" /> {r.email}
                          </a>
                        )}
                        {r.phone && (
                          <a
                            href={`tel:${r.phone}`}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            <Phone className="w-3 h-3" /> {r.phone}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-600">
                      {r.category || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {r.registered_at || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-bold ${
                          (r.days_waiting ?? 0) >= 90
                            ? "text-rose-600"
                            : (r.days_waiting ?? 0) >= 30
                            ? "text-amber-600"
                            : "text-gray-500"
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        {r.days_waiting != null ? `${r.days_waiting} day(s)` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.fee_paid ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                          <Wallet className="w-3 h-3" /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-400">
                          <Wallet className="w-3 h-3" /> Not paid
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Correcting the address is the gentler of the two and
                            the only one offered on an account that has paid, so
                            it comes first. */}
                        <AccessGate perm={PERMS.member.update}>
                          <button
                            onClick={() => setEmailFor(r)}
                            title="Change the email address on this account"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                          >
                            <Pencil className="w-3 h-3" /> Email
                          </button>
                        </AccessGate>

                        {/* Deleting destroys a record and cannot be undone, so
                            it asks for an admin role on top of the page's own
                            permission — the same pair the server requires. */}
                        <AccessGate
                          perm={PERMS.logbook_diet.access}
                          roles={["admin", "super_admin"]}
                        >
                          <button
                            onClick={() => setDeleteFor(r)}
                            title={
                              (r.discard_blockers?.length ?? 0) > 0
                                ? "This account cannot be deleted — open to see why"
                                : "Delete this account"
                            }
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-widest ${
                              (r.discard_blockers?.length ?? 0) > 0
                                ? "border-gray-200 text-gray-300 hover:text-gray-400"
                                : "border-rose-200 text-rose-600 hover:bg-rose-50"
                            }`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </AccessGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold text-gray-400">
              Page {meta.current_page} of {meta.last_page} · {meta.total} account(s) match
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={meta.current_page <= 1 || isFetching}
                className="px-3 py-2 rounded-lg border text-xs font-bold text-gray-600 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, meta.last_page))}
                disabled={meta.current_page >= meta.last_page || isFetching}
                className="px-3 py-2 rounded-lg border text-xs font-bold text-gray-600 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {emailFor && (
        <ChangeEmailDialog
          row={emailFor}
          onClose={() => setEmailFor(null)}
          onDone={(email) => {
            setEmailFor(null);
            setNotice(`Account moved to ${email}.`);
            refetch();
          }}
        />
      )}

      {deleteFor && (
        <DeleteAccountDialog
          row={deleteFor}
          onClose={() => setDeleteFor(null)}
          onChangeEmailInstead={() => {
            const row = deleteFor;
            setDeleteFor(null);
            setEmailFor(row);
          }}
          onDone={(who) => {
            setDeleteFor(null);
            setNotice(`Account ${who} deleted.`);
            refetch();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Correcting the address on an account                                    */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * Moves the sign-in address of an account that has not applied yet.
 *
 * This is the answer for the commonest reason an account on this list is
 * stuck: the address it was opened with is wrong or unreachable, so the person
 * never got the verification mail and could never come back to the form. It is
 * also the ONLY thing offered where the Institute has been paid, because that
 * account must not be deleted.
 *
 * The server moves the address across the account, the member record if there
 * is one, and any applications attached — and refuses an address already held
 * by another account, member or application, which is what the field error
 * below is reporting.
 */
function ChangeEmailDialog({
  row,
  onClose,
  onDone,
}: {
  row: AwaitingApplicationRow;
  onClose: () => void;
  onDone: (email: string) => void;
}) {
  const [email, setEmail] = useState(row.email ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [updateUserEmail, { isLoading }] = useUpdateUserEmailMutation();

  const save = async () => {
    const next = email.trim();
    setErr(null);

    if (!next) return setErr("Enter the address this account should use.");
    if (next === (row.email ?? "")) return setErr("That is the address it already has.");

    try {
      await updateUserEmail({ userId: Number(row.id), email: next }).unwrap();
      onDone(next);
    } catch (e: any) {
      setErr(
        e?.data?.errors?.email?.[0] ||
          e?.data?.message ||
          "Could not change that address.",
      );
    }
  };

  return (
    <Dialog onClose={onClose} title="Change the email on this account">
      <p className="text-sm text-gray-600">
        <span className="font-bold text-gray-900">{row.name || "This account"}</span> signs
        in with <span className="font-semibold">{row.email || "no address"}</span>.
      </p>

      <label className="block mt-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
        New email address
      </label>
      <input
        type="email"
        value={email}
        autoFocus
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-100"
        placeholder="name@example.com"
      />

      <p className="mt-2 text-[11px] font-semibold text-gray-400">
        Their application, if they ever file one, follows the new address. Tell them
        it has changed — it is what they will sign in with.
      </p>

      {err && <p className="mt-3 text-xs font-bold text-rose-600">{err}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3 py-2 rounded-lg border text-xs font-bold text-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
        >
          {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {isLoading ? "Saving…" : "Save address"}
        </button>
      </div>
    </Dialog>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Deleting an account that never applied                                  */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * Deletes a signup outright — or explains why it may not be, and sends the
 * desk to the email correction instead.
 *
 * Two screens in one, chosen by what the row says the server will allow:
 *
 *   blocked   the Institute holds something that cannot be reconstructed from
 *             anywhere else — money received, a logbook, a member record. The
 *             account stays, and the way out of a wrong address is to correct
 *             it, which is offered right here rather than described.
 *
 *   allowed   nothing on it but an unpaid invoice and abandoned payment
 *             attempts. What is about to go is listed in full, and DELETE has
 *             to be typed: this cannot be undone, and an OK button is too easy
 *             to press by reflex.
 *
 * The server checks the same rules again when the button is pressed. It has to
 * — a payment can land between the list loading and the press — and a refusal
 * that arrives late is shown in the same place as one that was known up front.
 */
function DeleteAccountDialog({
  row,
  onClose,
  onDone,
  onChangeEmailInstead,
}: {
  row: AwaitingApplicationRow;
  onClose: () => void;
  onDone: (who: string) => void;
  onChangeEmailInstead: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // Starts as what the list said, and is replaced if the server refuses on
  // grounds the list did not know about.
  const [blockers, setBlockers] = useState<string[]>(row.discard_blockers ?? []);
  const [discard, { isLoading }] = useDiscardDuplicateAccountMutation();

  const blocked = blockers.length > 0;
  const who = row.email || row.name || "this account";

  const remove = async () => {
    setErr(null);

    try {
      await discard({ userId: Number(row.id) }).unwrap();
      onDone(who);
    } catch (e: any) {
      const refused: string[] = e?.data?.blockers ?? [];

      if (refused.length) {
        // Not an error to apologise for — it is the rule working. The dialog
        // turns into the advice instead.
        setBlockers(refused);
        setTyped("");
        return;
      }

      setErr(e?.data?.message || "Could not delete that account.");
    }
  };

  return (
    <Dialog
      onClose={onClose}
      title={blocked ? "This account cannot be deleted" : "Delete this account"}
      tone={blocked ? "amber" : "rose"}
    >
      <p className="text-sm text-gray-600">
        <span className="font-bold text-gray-900">{row.name || "This account"}</span>
        {row.email ? ` — ${row.email}` : ""}
      </p>

      {blocked ? (
        <>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-black uppercase tracking-widest text-amber-700">
              Why
            </p>
            <ul className="mt-1.5 space-y-1">
              {blockers.map((b, i) => (
                <li key={i} className="text-sm font-semibold text-amber-900">
                  · {b}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-4 text-sm text-gray-600">
            The Institute holds something against this account that deleting it would
            destroy. If the problem is that the address is wrong or unreachable,{" "}
            <span className="font-bold text-gray-900">change the email instead</span> —
            the account keeps its payment and the person can sign in and finish the
            form.
          </p>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border text-xs font-bold text-gray-600"
            >
              Close
            </button>
            <button
              onClick={onChangeEmailInstead}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700"
            >
              <Pencil className="w-3.5 h-3.5" /> Change the email instead
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3">
            <p className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-rose-700">
              <ShieldAlert className="w-3.5 h-3.5" /> This deletes their history
            </p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-rose-900">
              <li>· The account itself, and their ability to sign in.</li>
              <li>· Every invoice raised against it, including the unpaid application fee.</li>
              <li>· Pending, failed and abandoned payment attempts.</li>
              <li>· Any part-filled application, with the documents uploaded to it.</li>
            </ul>
            <p className="mt-2 text-xs font-bold text-rose-700">
              It cannot be undone.
            </p>
          </div>

          <label className="block mt-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
            Type DELETE to confirm
          </label>
          <input
            value={typed}
            autoFocus
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && typed === "DELETE" && remove()}
            className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-100"
            placeholder="DELETE"
          />

          {err && <p className="mt-3 text-xs font-bold text-rose-600">{err}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border text-xs font-bold text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={remove}
              disabled={typed !== "DELETE" || isLoading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-40"
            >
              {isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              {isLoading ? "Deleting…" : "Delete account"}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** The one modal shell both dialogs sit in. */
function Dialog({
  title,
  tone = "gray",
  onClose,
  children,
}: {
  title: string;
  tone?: "gray" | "rose" | "amber";
  onClose: () => void;
  children: ReactNode;
}) {
  const tones = {
    gray: "text-gray-900",
    rose: "text-rose-700",
    amber: "text-amber-700",
  } as const;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      // A press on the backdrop closes; one inside the card must not.
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className={`text-lg font-black tracking-tight ${tones[tone]}`}>{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: "amber" | "rose" | "gray";
  hint?: string;
}) {
  const tones = {
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    rose: "bg-rose-50 border-rose-100 text-rose-700",
    gray: "bg-gray-50 border-gray-100 text-gray-700",
  } as const;

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">
        {label}
      </div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {hint && <div className="text-[11px] font-semibold opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function AdminAccountsAwaitingApplicationPage() {
  return (
    <PageGate perm={PERMS.logbook_diet.access}>
      <AwaitingApplicationScreen />
    </PageGate>
  );
}
